import type { SchemaField } from "../types";

/**
 * The visual groups each step draws its fields into (Founder: Figma `426:15238`, Project: Figma
 * `430:17237`).
 *
 * ## Why this is a mirror, and why it is nonetheless safe
 *
 * ⚠️ The groups are NOT in the API. Applicant fields carry `label`, `input_type`, `display_order`
 * and their condition — nothing that says which heading they belong under. The design nevertheless
 * groups them, and each heading carries a sentence of guidance that is doing real work: « Des faits,
 * pas des intentions » sets the register for the commitment questions, and « Ces réponses ne sont pas
 * notées » is required outright by US-E3-05 step 6.
 *
 * So the membership is written here, once per step. That is exactly the shape of the
 * `EDUCATION_LEVEL_CODES` mistake (rules/rounds.md), and the difference that makes it acceptable is
 * the FALLBACK: a field this table does not mention is not dropped, it renders in a final ungrouped
 * block. So a field the backend adds, renames or moves appears on day one — merely without a
 * heading — and can never be silently lost. Nothing here can affect a write, either: it decides
 * layout, never payload.
 *
 * **The real fix is a `group` on the applicant field contract.** When Aymen adds it, this file
 * deletes itself and the grouping becomes the round's to decide, like everything else.
 *
 * Order within a group follows the server's `display_order`, not this list — so a reordering
 * upstream is still honoured.
 */
const FOUNDER_GROUPS = [
  {
    key: "eligibility",
    fields: ["wilaya_code", "residency_confirmed", "age"],
  },
  {
    /** i18n key suffix under `dossier.groups.*`. */
    key: "commitment",
    fields: [
      "weekly_commitment_code",
      "current_situation_code",
      "situation_change_code",
      "training_attendance_code",
      "travel_feasibility_code",
      "subsistence_cost_coverage_code",
    ],
  },
  {
    key: "journey",
    fields: ["mentor_support_code"],
  },
  {
    key: "context",
    fields: [
      "activity_sector_code",
      "education_level_code",
      "education_level_other_text",
      "gender_code",
      "acquisition_source_code",
    ],
  },
  {
    key: "language",
    fields: ["dossier_language_code"],
  },
] as const;

/**
 * Project's four groups. ⚠️ NOT the same field list the old (pre-schema-v6) integration guide
 * describes — that doc's "Stade" also names `activity_sector_code` and a separate "Project
 * identity/narrative" group (`name`, `description`, `website_url`). Neither exists in this round's
 * `schema.applicant.sections[project].fields`, neither appears anywhere in the Figma frame, and
 * neither is in `missing_items` — checked against the running application, not assumed. Rendering
 * them would be inventing a screen nobody asked for; the doc is simply stale against the live
 * schema, and the frozen round's own field list is what this build follows.
 *
 * "3. Stade" carries no guidance sentence in the frame — a heading with no `help` key is valid, and
 * the renderer must not require one.
 */
const PROJECT_GROUPS = [
  {
    key: "validation",
    fields: [
      "client_count_code",
      "validation_period_code",
      "surprising_customer_insight",
      "market_signal_code",
    ],
  },
  {
    key: "viability",
    fields: [
      "payer_type_code",
      "revenue_range_code",
      "funding_status_code",
      "registration_status_code",
    ],
  },
  {
    key: "stage",
    fields: ["stage_code"],
  },
  {
    key: "impact",
    fields: ["impact_beneficiary_group", "impact_change", "impact_domain_code"],
  },
] as const;

/**
 * Team has no visual groups — its four fields (`mode`, `other_candidature_code`,
 * `owner_rationale`, `named_members_consent_confirmed`) render as one plain list, unlike
 * Founder/Project's numbered headings. `DossierSectionStep` special-cases Team further still:
 * `named_members_consent_confirmed` is pulled OUT of this list entirely and rendered after the
 * member roster, because the roster itself sits between "why is this dossier in your name" and
 * "confirm everyone named agrees" — and the roster is not a schema field at all, so nothing here
 * can express that ordering.
 */
const TEAM_GROUPS = [] as const;

/**
 * Practical's four groups (Figma `432:18482`). "4. Organisation pratique" is the only heading
 * that carries a guidance sentence in the frame — the other three stand alone, same as Project's
 * "3. Stade".
 *
 * `availability_codes` is the first `MULTI_SELECT` field this dossier renders — see
 * `DossierField.tsx`'s own case for that.
 *
 * ⚠️ `accessibility_requested` has no separate control in the Figma frame — only
 * `accessibility_details`'s free-text field is drawn. It is a real field in the live schema
 * (checked against the running application, same diligence as Project's stale-doc note above),
 * so it renders grouped alongside the text it plainly gates rather than falling to the ungrouped
 * fallback, which would separate two fields about the same question.
 */
const PRACTICAL_GROUPS = [
  {
    key: "contact",
    fields: ["mobile_phone"],
  },
  {
    key: "interview",
    fields: [
      "interview_language_code",
      "interview_mode_code",
      "internet_quality_code",
    ],
  },
  {
    key: "availability",
    fields: ["availability_codes"],
  },
  {
    key: "logistics",
    fields: [
      "travel_assistance",
      "accommodation_assistance",
      "accessibility_requested",
      "accessibility_details",
    ],
  },
] as const;

/** Every step's group config, keyed the same as `schema.applicant.sections[].key`. */
const GROUPS_BY_SECTION = {
  founder: FOUNDER_GROUPS,
  project: PROJECT_GROUPS,
  team: TEAM_GROUPS,
  practical: PRACTICAL_GROUPS,
} as const;

export type GroupedSectionKey = keyof typeof GROUPS_BY_SECTION;

/**
 * Is this section's grouping written yet? All four are now — `founder`, `project`, `team`, and
 * `practical` all have a real (if sometimes empty) entry in `GROUPS_BY_SECTION`.
 *
 * ⚠️ This is NOT the same question as "has this step been reconnected onto the new foundation
 * shell yet" — `views/dossier/DossierFoundationScreen.tsx`'s `RECONNECTED_STEPS` answers that one,
 * deliberately kept separate and hand-maintained so a mapping existing here is never mistaken for
 * a decision to expose it. See that file's own doc comment for why the distinction is load-bearing.
 */
export function hasFieldGroups(
  sectionKey: string
): sectionKey is GroupedSectionKey {
  return sectionKey in GROUPS_BY_SECTION;
}

export type FieldGroupKey =
  (typeof GROUPS_BY_SECTION)[GroupedSectionKey][number]["key"];

/** One rendered group: a heading key (or `null` for the fallback) and the fields under it. */
export interface FieldGroup {
  /** `null` means "not in the map" — rendered with no heading, so nothing is lost. */
  key: FieldGroupKey | null;
  fields: SchemaField[];
}

/**
 * Sort one step's fields into ITS OWN design groups, keeping server order inside each.
 *
 * Anything unmapped lands in a trailing group with `key: null`. An empty group is omitted, so a
 * round that switched a whole group's fields off does not leave a heading over nothing.
 */
export function groupSectionFields(
  sectionKey: GroupedSectionKey,
  fields: SchemaField[]
): FieldGroup[] {
  const config = GROUPS_BY_SECTION[sectionKey];
  const ordered = [...fields].sort(
    (a, b) => a.display_order - b.display_order || a.id.localeCompare(b.id)
  );

  const groups: FieldGroup[] = config
    .map((group) => ({
      key: group.key as FieldGroupKey,
      fields: ordered.filter((field) =>
        (group.fields as readonly string[]).includes(field.id)
      ),
    }))
    .filter((group) => group.fields.length > 0);

  const mapped = new Set(
    config.flatMap((group) => group.fields as readonly string[])
  );
  const orphans = ordered.filter((field) => !mapped.has(field.id));
  if (orphans.length > 0) groups.push({ key: null, fields: orphans });

  return groups;
}
